import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import { type FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addSavedAddress, removeSavedAddress } from 'state/futures/actions'
import { selectSavedAddresses } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import styled from 'styled-components'
import { isAddress } from 'viem'
import Button from './Button'
import Input from './Input/Input'
import Body from './Text/Body'

type AddressBookProps = {
	withdrawAddress: string
	onWithdrawAddressChange: (value: string) => void
}

const AddressBook: FC<AddressBookProps> = ({ withdrawAddress, onWithdrawAddressChange }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const savedAddresses = useAppSelector(selectSavedAddresses)
	const [open, setOpen] = useState(false)

	const handleToggle = useCallback(() => {
		setOpen((prev) => !prev)
	}, [])

	const handleAddAddress = useCallback(() => {
		if (
			withdrawAddress &&
			isAddress(withdrawAddress) &&
			!savedAddresses.includes(withdrawAddress)
		) {
			dispatch(addSavedAddress(withdrawAddress))
		}
	}, [withdrawAddress, savedAddresses, dispatch])

	const handleRemoveAddress = useCallback(
		(address: string) => {
			dispatch(removeSavedAddress(address))
		},
		[dispatch]
	)

	return (
		<FlexDivCol rowGap="10px">
			<Body color="secondary">{t('account.withdraw-section.wallet-address')}</Body>
			<FlexDivCol rowGap="4px">
				<AddressInputContainer onClick={handleToggle}>
					<StyledInput
						placeholder="0x"
						value={withdrawAddress}
						onChange={(e) => onWithdrawAddressChange(e.target.value)}
					/>
					{isAddress(withdrawAddress) && !savedAddresses.includes(withdrawAddress) && (
						<AddressButton onClick={handleAddAddress}>+</AddressButton>
					)}
				</AddressInputContainer>
				{open && savedAddresses.length > 0 && (
					<SavedAddressesDropdown>
						{savedAddresses.map((address) => (
							<AddressItem key={address}>
								<AddressText
									onClick={() => {
										onWithdrawAddressChange(address)
										handleToggle()
									}}
								>
									{address}
								</AddressText>
								<AddressButton onClick={() => handleRemoveAddress(address)}>-</AddressButton>
							</AddressItem>
						))}
					</SavedAddressesDropdown>
				)}
			</FlexDivCol>
		</FlexDivCol>
	)
}

const AddressButton = styled(Button)`
  width: 24px;
  height: 24px;
  padding: 0 8px;
`

const SavedAddressesDropdown = styled.div`
  border-radius: 8px;
  max-height: 150px;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.primary.background};
  border: ${(props) => props.theme.colors.selectedTheme.input.border};
  border-radius: 8px;
`

const AddressItem = styled(FlexDivRow)`
  padding: 10px;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.selectedTheme.border};
  &:last-child {
    border-bottom: none;
  }
  cursor: pointer;
  color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
  &:hover {
    background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
  }
`

const AddressText = styled.div``

const AddressInputContainer = styled(FlexDivRow)`
  gap: 10px;
  align-items: center;
`

const StyledInput = styled(Input)`
  width: 100%;
  font-size: 13px;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
`

export default AddressBook
